import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField, Select } from "@shopify/polaris";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import { SUPPLY_KINDS } from "utils/pvmClassifier";

/**
 * Manual entry and correction.
 *
 * This form is what stops the module being a dead end. Extraction fails on
 * scanned faxes, password-protected files and photographs taken at an angle,
 * and a company without an API key gets no extraction at all — if the only way
 * in were automatic, the accountant would go back to typing invoices into the
 * accounting system and the register would become a place documents go to be
 * ignored.
 *
 * Only changed fields are sent, so an edit of one number cannot silently
 * overwrite a field somebody else corrected a minute ago.
 */
type Props = {
  detail: PurchaseDocDetail;
  disabled?: boolean;
  onSave: (changes: Record<string, string | number | null>) => void;
  onCancel: () => void;
};

const DOC_KINDS = [
  "invoice",
  "credit_note",
  "debit_note",
  "proforma",
  "advance",
  "statement",
  "receipt",
  "not_a_document",
];

function asText(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

export function asDateInput(value: unknown): string {
  if (!value) return "";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

export default function InvoiceDocForm({ detail, disabled, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const doc = detail.doc;

  const initial: Record<string, string> = {
    docKind: asText(doc.docKind) || "invoice",
    // Empty = nobody has said; goes out as null like any other emptied field.
    supplyKind: asText(doc.supplyKind),
    supplierNameRaw: asText(doc.supplierNameRaw),
    supplierRegCodeRaw: asText(doc.supplierRegCodeRaw),
    supplierVatCodeRaw: asText(doc.supplierVatCodeRaw),
    supplierIbanRaw: asText(doc.supplierIbanRaw),
    invoiceSeries: asText(doc.invoiceSeries),
    invoiceNumber: asText(doc.invoiceNumber),
    issueDate: asDateInput(doc.issueDate),
    dueDate: asDateInput(doc.dueDate),
    currency: asText(doc.currency) || "EUR",
    netAmount: asText(doc.netAmount),
    vatAmount: asText(doc.vatAmount),
    grossAmount: asText(doc.grossAmount),
    // Empty = the Bank of Lithuania rate for the invoice date, downloaded by
    // the backend. Only a MANUAL rate is shown here, so saving the form never
    // turns a downloaded rate into a hand-typed one.
    fxRate: doc.fxRateSource === "manual" ? asText(doc.fxRate) : "",
  };

  const [values, setValues] = useState<Record<string, string>>(initial);

  const currencyKey = (code: string) => code.trim().toUpperCase();
  const sameCurrency = (a: string, b: string) => currencyKey(a) === currencyKey(b);

  // A rate belongs to the currency it was typed for. The rate field is kept
  // per currency: switching away parks the current value under the old code,
  // switching to a code shows whatever is parked there (the stored manual
  // rate for the document's own currency, empty = automatic for any other).
  // So a dollar rate is never saved onto a pound or euro document, and a
  // case edit ("usd") or retyping the code does not lose a typed rate.
  const ratesByCurrency = useRef<Record<string, string>>({ [currencyKey(initial.currency)]: initial.fxRate });

  const set = (key: string) => (value: string) =>
    setValues((current) => {
      if (key !== "currency") return { ...current, [key]: value };
      const from = currencyKey(current.currency);
      const to = currencyKey(value);
      if (from === to) return { ...current, currency: value };
      ratesByCurrency.current[from] = current.fxRate;
      return { ...current, currency: value, fxRate: ratesByCurrency.current[to] ?? "" };
    });

  const changed = Object.keys(values).filter((key) => values[key] !== initial[key]);

  const submit = () => {
    const changes: Record<string, string | number | null> = {};
    for (const key of changed) {
      const raw = values[key].trim();
      changes[key] = raw === "" ? null : raw;
    }
    onSave(changes);
  };

  const field = (key: string, labelKey: string, defaultLabel: string, type?: "number" | "date") => (
    <TextField
      key={key}
      label={t(labelKey, { defaultValue: defaultLabel })}
      value={values[key]}
      onChange={set(key)}
      autoComplete="off"
      type={type}
      // A field the backend could not verify is the one most likely to need
      // typing, so it says so rather than looking like every other field.
      helpText={
        (doc.needsReviewFields || []).indexOf(key) !== -1
          ? t("invoice_inbox_field_unverified", { defaultValue: "Could not be verified automatically" })
          : undefined
      }
    />
  );

  return (
    <div className="space-y-3 border rounded p-3 bg-gray-50">
      <Select
        label={t("invoice_inbox_doc_kind", { defaultValue: "Document type" })}
        options={DOC_KINDS.map((kind) => ({
          label: t(`invoice_inbox_kind_${kind}`, { defaultValue: kind }),
          value: kind,
        }))}
        value={values.docKind}
        onChange={set("docKind")}
        // Changing this changes the SIGN of the amounts: a credit note reduces
        // what we owe, and Lithuanian ones are printed positive.
        helpText={
          values.docKind === "credit_note"
            ? t("invoice_inbox_credit_note_hint", {
                defaultValue: "Stored as a negative amount — it reduces what we owe.",
              })
            : undefined
        }
      />

      {/* Decides the VAT classifier for a foreign supplier: goods from the EU
          are an acquisition, services a reverse charge. */}
      <Select
        label={t("invoice_inbox_supply_kind", { defaultValue: "Goods or services" })}
        options={[
          { label: t("invoice_inbox_supply_kind_unset", { defaultValue: "Not set" }), value: "" },
          ...SUPPLY_KINDS.map((kind) => ({
            label: t(`invoice_inbox_supply_kind_${kind}`, { defaultValue: kind }),
            value: kind,
          })),
        ]}
        value={values.supplyKind}
        onChange={set("supplyKind")}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {field("supplierNameRaw", "supplier", "Supplier")}
        {field("supplierRegCodeRaw", "company_code", "Company code")}
        {field("supplierVatCodeRaw", "vat_code", "VAT code")}
        {field("supplierIbanRaw", "iban", "IBAN")}
        {field("invoiceSeries", "invoice_inbox_series", "Series")}
        {field("invoiceNumber", "invoice_inbox_col_invoice_number", "Invoice no.")}
        {field("issueDate", "date", "Date", "date")}
        {field("dueDate", "due_date", "Due", "date")}
        {field("netAmount", "net", "Net", "number")}
        {field("vatAmount", "vat", "VAT", "number")}
        {field("grossAmount", "total", "Total", "number")}
        {field("currency", "currency", "Currency")}
        {!sameCurrency(values.currency, "EUR") && (
          <TextField
            label={t("invoice_inbox_fx_rate_field", { defaultValue: "Exchange rate (1 EUR = …)" })}
            value={values.fxRate}
            onChange={set("fxRate")}
            autoComplete="off"
            type="number"
            placeholder={
              doc.fxRateSource !== "manual" && doc.fxRate !== null && doc.fxRate !== undefined
                ? String(doc.fxRate)
                : undefined
            }
            helpText={t("invoice_inbox_fx_rate_help", {
              defaultValue:
                "Leave empty to use the Bank of Lithuania rate for the invoice date. Fill in only to override it.",
            })}
          />
        )}
      </div>

      <div className="flex gap-2">
        <Button variant="primary" disabled={disabled || !changed.length} onClick={submit}>
          {t("save", { defaultValue: "Save" })}
        </Button>
        <Button disabled={disabled} onClick={onCancel}>
          {t("cancel", { defaultValue: "Cancel" })}
        </Button>
        {changed.length > 0 && (
          <span className="self-center text-xs text-gray-500">
            {t("invoice_inbox_changed_fields", {
              defaultValue: "{{count}} field(s) changed",
              count: changed.length,
            })}
          </span>
        )}
      </div>

      {doc.approvalSnapshotHash && changed.length > 0 && (
        <p className="text-xs text-amber-700">
          {t("invoice_inbox_edit_invalidates", {
            defaultValue:
              "This document has already been approved by someone. Changing an amount, the account or the number withdraws those approvals — they described the document as it was.",
          })}
        </p>
      )}
    </div>
  );
}
